import { Link } from 'react-router-dom'
import type { ShowStats, Stats } from '../api/types'
import { formatCount, shortDay } from '../utils/format'
import { BarChart } from './BarChart'
import { Card, ErrorNote, LoadingBlock, Stat } from './ui'

interface Query {
  data?: Stats | ShowStats
  isLoading: boolean
  isError: boolean
  error: unknown
}

function topOf(stats: Stats | ShowStats) {
  return 'top_episodes' in stats ? stats.top_episodes : []
}

/**
 * Downloads over time, for a show or for one episode. The same card serves
 * both: a show also shows feed fetches and its best-heard episodes.
 */
export function ListenersCard({ query, title = 'Listeners', days = 30 }: { query: Query; title?: string; days?: number }) {
  if (query.isLoading) return <LoadingBlock />
  if (query.isError) return <ErrorNote error={query.error} />
  if (!query.data) return null
  const stats = query.data
  const top = topOf(stats)
  const feed = 'top_episodes' in stats
  const quiet = stats.downloads_all_time === 0
  return (
    <Card title={title}>
      {quiet ? (
        <p className="muted">
          Nobody has downloaded this yet. Downloads are counted once per listener a day, the way podcast apps are measured, and cost no credits.
        </p>
      ) : (
        <div className="stack">
          <div className={feed ? 'grid-3' : 'grid-2'}>
            <Stat label={`Downloads, ${days} days`} value={formatCount(stats.downloads)} note={`${shortDay(stats.from)} to ${shortDay(stats.to)}`} />
            <Stat label="Downloads, all time" value={formatCount(stats.downloads_all_time)} note="Since counting began" />
            {feed && <Stat label="Feed fetches" value={formatCount(stats.feed_fetches)} note="Podcast apps checking for new episodes" />}
          </div>
          <BarChart
            data={stats.days}
            label={(day) => shortDay(day.day)}
            format={formatCount}
            series={[{ label: 'Downloads', color: 'var(--accent)', value: (day) => day.downloads }]}
          />
          {top.length > 0 && (
            <div className="stack-sm">
              <h3 className="small muted">Heard most</h3>
              <ul className="asset-list">
                {top.map((episode) => (
                  <li key={episode.episode_id}>
                    <Link to={`/studio/episodes/${episode.episode_id}`}>{episode.title}</Link>
                    <span className="muted">{formatCount(episode.downloads)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
          <p className="muted small">
            One download per listener a day, robots left out. We hand the file to your listener's app, so this counts downloads that started, not
            minutes heard.
          </p>
        </div>
      )}
    </Card>
  )
}
